import type { HomeEconomy } from "@/home/economy";
import type { HomeStructureCombatSnapshot } from "@/home/home-structure-types";
import {
  applyHomeStructureDefCard,
  applyHomeStructureHpCard,
  applyHomeStructureRegenCard,
  homeStructureDefCards,
  homeStructureHpCards,
  homeStructureHpPotions,
  homeStructureRegenCards,
  setHomeStructureAutoPotionEnabled,
  setHomeStructureAutoPotionHpPercent,
} from "@/home/home-structure-economy";
import { getGameSettings } from "@/game/model/game-settings";

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function formatShieldRemaining(brokenUntil: number, now: number): string {
  if (brokenUntil <= now) return "";
  const ms = brokenUntil - now;
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  return `${h} ชม. ${m} นาที`;
}

export function renderHomeStructureStatusModalHtml(
  snapshot: HomeStructureCombatSnapshot,
  economy: HomeEconomy,
): string {
  const gs = getGameSettings();
  const hpPct =
    snapshot.maxHp > 0 ? Math.round((snapshot.currentHp / snapshot.maxHp) * 100) : 0;
  const shieldText = snapshot.shieldActive
    ? formatShieldRemaining(snapshot.brokenUntil, Date.now())
    : "";
  const autoPct = economy.homeStructureAutoPotionHpPercent ?? 50;
  const autoOn = economy.homeStructureAutoPotionEnabled !== false;

  return `
    <div id="home-structure-status-modal" class="home-cash-shop-modal home-structure-modal" hidden aria-hidden="true">
      <button type="button" class="home-cash-shop-backdrop" data-close-structure-status aria-label="ปิด"></button>
      <div class="home-cash-shop-dialog home-structure-dialog" role="dialog" aria-modal="true" aria-labelledby="home-structure-title">
        <header class="home-structure-dialog-head">
          <h2 id="home-structure-title" class="home-structure-title">สเตตัสบ้านป้องกัน</h2>
          <button type="button" class="dungeon-frame-close" data-close-structure-status aria-label="ปิด">×</button>
        </header>
        <p class="hint home-structure-lede">เป้าสุดท้ายเมื่อถูกโจมตี · ยาบ้านใช้อัตโนมัติได้แม้คุณไม่ออนไลน์</p>
        <div class="home-structure-vitals">
          <div class="home-structure-hp-row">
            <span class="home-structure-hp-label">HP บ้าน</span>
            <span class="home-structure-hp-value" id="home-structure-hp-text">${snapshot.currentHp.toLocaleString("en-US")} / ${snapshot.maxHp.toLocaleString("en-US")} (${hpPct}%)</span>
          </div>
          <div class="home-structure-hp-bar" aria-hidden="true">
            <span class="home-structure-hp-fill" id="home-structure-hp-fill" style="width:${hpPct}%"></span>
          </div>
          <dl class="home-structure-stat-grid">
            <div><dt>ป้องกัน</dt><dd id="home-structure-def">${snapshot.defense}</dd></div>
            <div><dt>ฟื้น/วิ</dt><dd id="home-structure-regen">${snapshot.regenPerSec.toFixed(2)}</dd></div>
            <div><dt>ยา HP บ้าน</dt><dd id="home-structure-potions">${homeStructureHpPotions(economy)}</dd></div>
          </dl>
          ${shieldText ? `<p class="home-structure-shield hint" id="home-structure-shield">เกราะฟื้นฟู · ไม่รับโจมตีอีก ${escapeHtml(shieldText)}</p>` : `<p class="home-structure-shield hint" id="home-structure-shield" hidden></p>`}
        </div>
        <section class="home-structure-cards" aria-label="บัตรอัพเกรด">
          <h3 class="home-structure-section-title">บัตรอัพ (Cash)</h3>
          <div class="home-structure-card-actions">
            <button type="button" class="btn btn-primary btn-sm" data-apply-structure-card="hp">ใช้บัตร HP (${homeStructureHpCards(economy)})</button>
            <button type="button" class="btn btn-primary btn-sm" data-apply-structure-card="def">ใช้บัตรป้องกัน (${homeStructureDefCards(economy)})</button>
            <button type="button" class="btn btn-primary btn-sm" data-apply-structure-card="regen">ใช้บัตรฟื้น (${homeStructureRegenCards(economy)})</button>
          </div>
          <p class="hint">+${gs.homeStructureHpPerCard} HP · +${gs.homeStructureDefPerCard} DEF · +${gs.homeStructureRegenPerCard}/วิ ต่อใบ</p>
        </section>
        <section class="home-structure-potion" aria-label="ยาบ้าน">
          <h3 class="home-structure-section-title">ยา HP บ้าน (ทอง)</h3>
          <label class="home-structure-auto">
            <input type="checkbox" id="home-structure-auto-enabled" ${autoOn ? "checked" : ""} />
            ใช้อัตโนมัติเมื่อ HP ≤
            <input type="number" class="input input-inline" id="home-structure-auto-pct" min="1" max="99" value="${autoPct}" />
            %
          </label>
          <button type="button" class="btn btn-ghost btn-sm" id="home-structure-use-potion" ${homeStructureHpPotions(economy) <= 0 ? "disabled" : ""}>
            ใช้ยาตอนนี้ (+${gs.homeStructurePotionHeal} HP)
          </button>
          <p class="hint" id="home-structure-status-msg" role="status"></p>
        </section>
      </div>
    </div>
  `;
}

export type HomeStructureStatusModalHandlers = {
  getEconomy: () => HomeEconomy;
  setEconomy: (economy: HomeEconomy) => void;
  getSnapshot: () => HomeStructureCombatSnapshot;
  refreshSnapshot: () => HomeStructureCombatSnapshot;
  onEconomyChanged: () => void;
  onUsePotion: () => Promise<string | null>;
};

export function bindHomeStructureStatusModal(
  root: HTMLElement,
  handlers: HomeStructureStatusModalHandlers,
): { open: () => void; close: () => void; refresh: () => void } {
  const modal = () => root.querySelector<HTMLElement>("#home-structure-status-modal");
  const msg = () => root.querySelector<HTMLElement>("#home-structure-status-msg");

  const refresh = () => {
    const snap = handlers.refreshSnapshot();
    const hpPct = snap.maxHp > 0 ? Math.round((snap.currentHp / snap.maxHp) * 100) : 0;
    const hpText = root.querySelector("#home-structure-hp-text");
    const fill = root.querySelector<HTMLElement>("#home-structure-hp-fill");
    const def = root.querySelector("#home-structure-def");
    const regen = root.querySelector("#home-structure-regen");
    const potions = root.querySelector("#home-structure-potions");
    if (hpText) {
      hpText.textContent = `${snap.currentHp.toLocaleString("en-US")} / ${snap.maxHp.toLocaleString("en-US")} (${hpPct}%)`;
    }
    if (fill) fill.style.width = `${hpPct}%`;
    if (def) def.textContent = String(snap.defense);
    if (regen) regen.textContent = snap.regenPerSec.toFixed(2);
    if (potions) potions.textContent = String(homeStructureHpPotions(handlers.getEconomy()));
    const shield = root.querySelector<HTMLElement>("#home-structure-shield");
    if (shield) {
      const text = snap.shieldActive
        ? `เกราะฟื้นฟู · ไม่รับโจมตีอีก ${formatShieldRemaining(snap.brokenUntil, Date.now())}`
        : "";
      shield.textContent = text;
      shield.hidden = !text;
    }
  };

  const close = () => {
    const el = modal();
    if (!el) return;
    el.hidden = true;
    el.setAttribute("aria-hidden", "true");
    root.classList.remove("home-structure-modal-open");
  };

  const open = () => {
    const el = modal();
    if (!el) return;
    refresh();
    el.hidden = false;
    el.setAttribute("aria-hidden", "false");
    root.classList.add("home-structure-modal-open");
  };

  root.querySelectorAll("[data-close-structure-status]").forEach((btn) => {
    btn.addEventListener("click", () => close());
  });

  root.querySelectorAll<HTMLButtonElement>("[data-apply-structure-card]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const kind = btn.dataset.applyStructureCard;
      const economy = handlers.getEconomy();
      const res =
        kind === "hp"
          ? applyHomeStructureHpCard(economy)
          : kind === "def"
            ? applyHomeStructureDefCard(economy)
            : kind === "regen"
              ? applyHomeStructureRegenCard(economy)
              : null;
      if (!res) return;
      if (!res.ok) {
        if (msg()) msg()!.textContent = res.reason;
        return;
      }
      handlers.setEconomy(res.economy);
      handlers.onEconomyChanged();
      refresh();
      if (msg()) msg()!.textContent = "อัพเกรดบ้านแล้ว";
    });
  });

  root.querySelector("#home-structure-auto-enabled")?.addEventListener("change", (event) => {
    const checked = (event.target as HTMLInputElement).checked;
    handlers.setEconomy(setHomeStructureAutoPotionEnabled(handlers.getEconomy(), checked));
    handlers.onEconomyChanged();
  });

  root.querySelector("#home-structure-auto-pct")?.addEventListener("change", (event) => {
    const value = Number((event.target as HTMLInputElement).value);
    handlers.setEconomy(setHomeStructureAutoPotionHpPercent(handlers.getEconomy(), value));
    handlers.onEconomyChanged();
  });

  root.querySelector("#home-structure-use-potion")?.addEventListener("click", () => {
    void handlers.onUsePotion().then((err) => {
      if (err && msg()) msg()!.textContent = err;
      else if (msg()) msg()!.textContent = "ใช้ยาบ้านแล้ว";
      refresh();
    });
  });

  return { open, close, refresh };
}
